import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Agent, useAgentsService } from '../services/agents.service';
import { AgentCard } from '../components/agents/AgentCard';

export const AgentsPage = () => {
  const { t } = useTranslation('agents');
  const { getAgents, setAgentEnabled } = useAgentsService();
  const queryClient = useQueryClient();

  const {
    data: agents,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['agents'],
    queryFn: getAgents,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      setAgentEnabled(key, enabled),
    onSuccess: (enabled, variables) => {
      queryClient.setQueryData<Agent[]>(['agents'], (prev) =>
        prev?.map((a) =>
          a.key === variables.key ? { ...a, enabled } : a
        )
      );
      toast.success(
        enabled ? t('notifications.hired') : t('notifications.released'),
      );
    },
    onError: (error: Error) => {
      toast.error(error.message || t('notifications.update_failed'));
    },
  });

  return (
    <div className="bg-background-gray h-full overflow-y-auto">
      <div className="px-6 lg:px-10 py-6 md:py-10">
        <div className="mb-8 max-w-3xl">
          <h4 className="text-xl md:text-2xl font-semibold text-dark-invirtus font-heading leading-tight">
            {t('page.title')}
          </h4>
          <p className="mt-1 text-sm text-dark-invirtus/60 font-body">
            {t('page.subtitle')}
          </p>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-dark-invirtus/55 font-body">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('page.loading')}
          </div>
        )}

        {isError && (
          <div className="rounded-xl border border-red-soft bg-red-soft/40 px-4 py-3 text-sm text-dark-invirtus font-body">
            {t('page.load_error')}
          </div>
        )}

        {agents && (
          <div className="grid max-w-5xl gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {agents.map((agent) => (
              <AgentCard
                key={agent.key}
                agent={agent}
                isPending={
                  toggleMutation.isPending &&
                  toggleMutation.variables?.key === agent.key
                }
                onToggle={(enabled) =>
                  toggleMutation.mutate({ key: agent.key, enabled })
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
